import { PublicClientApplication } from "@azure/msal-browser";
import { attachAxiosErrorInterceptor } from "./setupAxios";
import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Dispatch } from "redux";
import { ComponentFields as CF } from '../src/core/services/ComponentService';
import B2CCallbackCatchContainer from "./accounts/components/B2C/B2CCallbackCatchContainer";
import LoginPageContainer from "./accounts/components/loginPage/LoginPageContainer";
import MSALCallbackCatchContainer from "./accounts/components/MSAL/MSALCallbackCatchContainer";
import { RegisterPage } from './accounts/components/RegisterPage';
import SignOutContainer from "./accounts/components/signOut/SignOutContainer";
import { MATCHING_ENGINE, MEMBERS_PORTAL } from "./accounts/Consts";
import { msalConfig } from "./accounts/types/msalAuthConfig";
import HeaderContainer from "./core/components/header/HeaderContainer";
import { SectionNotificationReset } from "./core/components/SectionNotificationReset";
import { PrivateRoute } from './core/components/PrivateRoute';
import { IMenuItem } from './core/types/IMenuItem';
import DataIngestionPageContainer from "./dataingestion/components/DataIngestionPageContainer";
import HomePageContainer from './home/components/HomePageContainer';
import MyAccountPageContainer from "./membersportal/account/MyAccountPageContainer";
import MyCasesPageContainer from "./membersportal/crmIntegrations/MyCasesPageContainer";
import MyFormsPageContainer from "./membersportal/crmIntegrations/MyFormsPageContainer";
import MembersPortalPageContainer from "./membersportal/MembersPortalPageContainer";
import RequestAccessPageContainer from "./membersportal/requestAccess/RequestAccessPageContainer";
import ViewAsPageContainer from "./membersportal/viewAs/ViewAsPageContainer";
import RepertoirePageContainer from './repertoire/RepertoirePageContainer';
import SettingsPageContainer from './settings/components/SettingsPageContainer';
import ValidationSettingsContainer from './settings/components/ValidationSettingsContainer';
import { useAuthRouteWatcher, IAuthRouteWatcherProps } from "./membersportal/components/hooks/useAuthRouteWatcher";
import { useIdleHandler } from "./membersportal/components/hooks/useIdleHandler";
import AnalyticsPageContainer from "./membersportal/statements/AnalyticsPageContainer";
import { initializeAppOnLoadThunk } from "./redux/thunks/AppInitializationThunks";
import { PageViewTracker } from "./telemetry/components/PageViewTracker";
import { CURRENT_USER_EMAIL_KEY } from "./redux/services/AccountSessionStorage";
import ScheduledJobsPageContainer from "./settings/components/ScheduledJobsPageContainer";
import EphemeralsPageContainer from "./settings/components/EphemeralsPageContainer";

interface IAppProps {
    portalType: string;
    dispatch: Dispatch;
    isTest?: boolean;
    instance?: PublicClientApplication;
    client?: string;
    sessionTimeoutInMinutes?: number;
}

interface IAppState {
    routes: JSX.Element[];
    hasInitializedApp: boolean;
}



const isMatchingEngineSessionAuthenticated = () =>
    sessionStorage.getItem("isSignedIn") === "true" ||
    sessionStorage.getItem("isAuthenticated") === "true";

const AuthRouteWatcher = ({ location, portalType }: IAuthRouteWatcherProps) => {
    useAuthRouteWatcher(location, portalType);
    return null;
};

const IdleHandler = ({ sessionTimeoutInMinutes }: { sessionTimeoutInMinutes?: number }) => {
    useIdleHandler(sessionTimeoutInMinutes);
    return null;
};

export default class App extends React.Component<IAppProps, IAppState> {
    constructor(props: IAppProps) {
        super(props);

        this.state = ({
            routes: [],
            hasInitializedApp: false
        });
    }

    private syncPortalBodyClass = (portalType: string) => {
        if (portalType === MEMBERS_PORTAL) {
            document.body.classList.add('members-portal');
            return;
        }

        document.body.classList.remove('members-portal');
    };

    private canInitialize = (): boolean => {
        const { portalType } = this.props;

        if (portalType === MEMBERS_PORTAL) {
            return sessionStorage.getItem("isSignedIn") === "true";
        }

        return sessionStorage.getItem("isAuthenticated") === "true";
    };

    initializeIfReady = () => {
        const { portalType, dispatch } = this.props;
        const { hasInitializedApp } = this.state;

        if (hasInitializedApp) {
            return;
        }

        if (!this.canInitialize()) {
            return;
        }

        if (portalType === MEMBERS_PORTAL) {
            const currentUserEmail = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY) || undefined;

            dispatch<any>(
                initializeAppOnLoadThunk({
                    portalType,
                    currentUserEmail,
                })
            );
        } else {
            dispatch<any>(
                initializeAppOnLoadThunk({
                    portalType,
                })
            );
        }

        this.setState({ hasInitializedApp: true });
    };

    componentDidMount() {
        const { portalType, dispatch, isTest, instance } = this.props;

        this.syncPortalBodyClass(portalType);

        if (portalType === MEMBERS_PORTAL) {
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
                instance.setActiveAccount(accounts[0]);
            }

            const scope = {
                scopes: [msalConfig.auth.scope]
            };

            if (instance && instance.getAllAccounts().length < 1) {
                instance
                    .handleRedirectPromise()
                    .then((tokenResponse) => {
                        if (!tokenResponse) {
                            const account = instance.getActiveAccount();
                            if (!account) {
                                instance.acquireTokenRedirect(scope);
                            }
                        }

                        this.initializeIfReady();
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            } else {
                this.initializeIfReady();
            }
        } else {
            this.initializeIfReady();
        }

        if (!isTest) {
            attachAxiosErrorInterceptor(instance);

            const menuItemsSession = sessionStorage.getItem('menuItems');
            const menuItemsParsed = menuItemsSession
                ? JSON.parse(menuItemsSession) as IMenuItem[]
                : null;

            if (menuItemsParsed && menuItemsParsed.length > 0) {
                this.setState({ routes: this.getRoutes(menuItemsParsed) });
            } else {
                CF.getMenuItems(portalType, dispatch)
                    .then(menuItems => {
                        sessionStorage.setItem('menuItems', JSON.stringify(menuItems));
                        this.setState({ routes: this.getRoutes(menuItems) });
                    });
            }
        }
    }

    componentDidUpdate(prevProps: IAppProps, prevState: IAppState) {
        if (!this.state.hasInitializedApp) {
            this.initializeIfReady();
        }

        if (prevProps.portalType !== this.props.portalType) {
            this.syncPortalBodyClass(this.props.portalType);
        }
    }

    componentWillUnmount() {
        document.body.classList.remove('members-portal');
    }

    getRoutes(menuItems: IMenuItem[]): JSX.Element[] {
        const { portalType, instance, client } = this.props;
        let routes: JSX.Element[] = [];

        let scope =
        {
            scopes: [msalConfig.auth.scope]
        }

        if (menuItems && menuItems.length > 0) {

            routes.push(<PrivateRoute key='/' exact path='/' component={HomePageContainer} instance={instance} portalType={portalType} client={client} />);

            const settings = menuItems.find(m => m.link == '/settings');

            if (settings) {
                settings.isEnabled && routes.push(<PrivateRoute key={settings.link} exact path={settings.link} component={SettingsPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const matching = settings.children.find(m => m.link == '/settings/matching');
                matching && matching.isEnabled && routes.push(<PrivateRoute key={matching.link} path={matching.link} component={SettingsPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const scheduledJobs = settings.children.find(m => m.link == '/settings/scheduledJobs');
                scheduledJobs && scheduledJobs.isEnabled && routes.push(<PrivateRoute key={scheduledJobs.link} path={scheduledJobs.link} component={ScheduledJobsPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const validation = settings.children.find(m => m.link == '/settings/validation');
                validation && validation.isEnabled && routes.push(<PrivateRoute key={validation.link} exact path={validation.link} component={ValidationSettingsContainer} instance={instance} portalType={portalType} scope={scope} />);

                const ephemerals = settings.children.find(m => m.link == '/settings/ephemerals');
                ephemerals && ephemerals.isEnabled && routes.push(<PrivateRoute key={ephemerals.link} path={ephemerals.link} component={EphemeralsPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const repertoire = menuItems.find(m => m.link == '/repertoire');
            repertoire && repertoire.isEnabled && routes.push(<PrivateRoute key={repertoire.link} path={'/repertoire/:submodule?/:datasource?/:id?'} component={RepertoirePageContainer} instance={instance} portalType={portalType} scope={scope} />);

            const modernIngestion = menuItems.find(m => m.link == '/modernIngestion');
            if (modernIngestion) {
                modernIngestion.isEnabled && routes.push(<PrivateRoute key={modernIngestion.link} exact path={modernIngestion.link} component={DataIngestionPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const dataIngestion = modernIngestion.children.find(c => c.link === '/modernIngestion/dataIngestion');
                dataIngestion && dataIngestion.isEnabled && repertoire.isEnabled && routes.push(<PrivateRoute key={dataIngestion.link} exact path={dataIngestion.link} component={DataIngestionPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const membersPortalWorks = menuItems.find(m => m.link == '/works');
            if (membersPortalWorks) {
                membersPortalWorks.isEnabled && routes.push(<PrivateRoute key={'/works'} path={'/works/:submodule?/:datasource?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const myAccount = menuItems.find(m => m.link == '/myAccount');
            if (myAccount) {

                const accountDetails = myAccount.children.find(m => m.link == '/myAccount/accountDetails');
                accountDetails && routes.push(<PrivateRoute key={'/myAccount/accountDetails'} exact path={'/myAccount/accountDetails'} component={MyAccountPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const requestAccess = myAccount.children.find(m => m.link == '/myAccount/requestAccess');
                requestAccess && routes.push(<PrivateRoute key={'/myAccount/requestAccess'} path={'/myAccount/requestAccess/:submodule?/:datasource?/:id?'} component={RequestAccessPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                const viewAs = myAccount.children.find(m => m.link == '/myAccount/viewAs');
                viewAs && routes.push(<PrivateRoute key={'/myAccount/viewAs'} path={'/myAccount/viewAs/:submodule?/:datasource?/:id?'} component={ViewAsPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const statements = menuItems.find(m => m.link == '/memberStatements');
            if (statements) {
                if (statements.isEnabled) {
                    routes.push(<PrivateRoute key={'/memberStatements/analytics'} exact path={'/memberStatements/analytics'} component={AnalyticsPageContainer} instance={instance} portalType={portalType} scope={scope} />);

                    routes.push(<PrivateRoute key={'/memberStatements'} path={'/memberStatements/:submodule?/:datasource?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
                }
            }

            const myForms = menuItems.find(m => m.link == '/myForms');
            if (myForms) {
                myForms.isEnabled && routes.push(<PrivateRoute key={'/myForms'} path={'/myForms/:submodule?/:id?'} component={MyFormsPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const memberPortalCases = menuItems.find(m => m.link == '/cases/viewMyCases');
            if (memberPortalCases) {
                memberPortalCases.isEnabled && routes.push(<PrivateRoute key={'/cases'} path={'/cases/:submodule?/:id?'} component={MyCasesPageContainer} instance={instance} portalType={portalType} scope={scope}/>);
            }

            const memberPortalProducts = menuItems.find(m => m.link == '/products');
            if (memberPortalProducts) {
                memberPortalProducts.isEnabled && routes.push(<PrivateRoute key={'/products'} path={'/products/:submodule?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const memberPortalClaims = menuItems.find(m => m.link == '/claims');
            if (memberPortalClaims) {
                memberPortalClaims.isEnabled && routes.push(<PrivateRoute key={'/claims'} path={'/claims/:submodule?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

            const memberPortalAgreements = menuItems.find(m => m.link == '/agreements');
            if (memberPortalAgreements) {
                memberPortalAgreements.isEnabled && routes.push(<PrivateRoute key={'/agreements'} path={'/agreements/:submodule?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
            }

                routes.push(<PrivateRoute key={'/members/mypackages'} path={'/members/mypackages/:submodule?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
                routes.push(<PrivateRoute key={'/mypackages'} path={'/mypackages/:submodule?/:id?'} component={MembersPortalPageContainer} instance={instance} portalType={portalType} scope={scope} />);
        }

        return routes;
    }

    render() {
        const { portalType, sessionTimeoutInMinutes } = this.props

        return (
            <>
                <Route
                    render={({ location }) =>
                        location.pathname === '/login'
                            ? <div className="emptyHeader" />
                            : <HeaderContainer />
                    }
                />
                {portalType === MEMBERS_PORTAL && <IdleHandler sessionTimeoutInMinutes={sessionTimeoutInMinutes} />}
                {portalType === MATCHING_ENGINE ? (
                    <Route
                        path='/login'
                        render={(routeProps) =>
                            isMatchingEngineSessionAuthenticated()
                                ? <Redirect to="/" />
                                : <LoginPageContainer {...routeProps} />
                        }
                    />
                ) : <></>}
                {portalType === MATCHING_ENGINE ? <Route path='/aadLogin' component={MSALCallbackCatchContainer} /> : <></>}
                <Route path='/aadB2CLogin' component={B2CCallbackCatchContainer} />
                {portalType === MATCHING_ENGINE ? <Route path='/register' component={RegisterPage} /> : <></>}
                <Route path='/signOut' component={SignOutContainer} />
                <Route render={({ location }) => (
                        <PageViewTracker location={location} portalType={portalType} />
                    )}
                />
                <Route render={({ location }) => (
                        <SectionNotificationReset location={location} portalType={portalType} dispatch={this.props.dispatch} />
                    )}
                />
                {portalType === MEMBERS_PORTAL && (
                    <Route
                        render={({ location }) => (
                            <AuthRouteWatcher location={location} portalType={portalType} />
                        )}
                    />
                )}
                <Switch>
                    {this.state.routes}
                </Switch>
            </>
        );
    }
}
